import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useT } from '../../i18n';
import { config } from '../../monetization/config';
import { useLifetime } from '../../monetization/useLifetime';
import { playCue } from '../feedback';

interface Props {
    open: boolean;
    onClose: () => void;
}

/**
 * The one "support" button, two meanings:
 *  - in the phone app: buy the lifetime unlock (removes ads), through the store's own billing;
 *  - on the web: a tip link. The web has no purchase, and the stores do not allow steering app users to outside payments.
 */
export const SupportDialog: React.FC<Props> = ({ open, onClose }) => {
    const t = useT();
    const lifetime = useLifetime();
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            aria-labelledby="support-title"
        >
            <DialogTitle id="support-title">{t('support.title')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Typography>{t('support.intro')}</Typography>
                    {lifetime.supported ? (
                        lifetime.owned ? (
                            <Alert severity="success" icon={<span>✨</span>}>
                                {t('support.owned')}
                            </Alert>
                        ) : (
                            <Stack spacing={1}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    disabled={lifetime.busy}
                                    onClick={() => {
                                        playCue('ui');
                                        void lifetime.buy();
                                    }}
                                    startIcon={
                                        lifetime.busy ? (
                                            <CircularProgress
                                                size={18}
                                                color="inherit"
                                            />
                                        ) : undefined
                                    }
                                >
                                    {t('support.lifetime', {
                                        price: lifetime.price ?? '',
                                    })}
                                </Button>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {t('support.lifetimeHint')}
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={() => void lifetime.restore()}
                                    disabled={lifetime.busy}
                                >
                                    {t('support.restore')}
                                </Button>
                                {lifetime.error && (
                                    <Alert severity="warning">
                                        {lifetime.error}
                                    </Alert>
                                )}
                            </Stack>
                        )
                    ) : (
                        <Box>
                            {config.donateUrl || config.pixUrl ? (
                                <Stack spacing={1}>
                                    {config.donateUrl && (
                                        <Button
                                            variant="contained"
                                            size="large"
                                            fullWidth
                                            component="a"
                                            href={config.donateUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => playCue('ui')}
                                        >
                                            {t('support.coffee')}
                                        </Button>
                                    )}
                                    {config.pixUrl && (
                                        <Button
                                            variant={
                                                config.donateUrl
                                                    ? 'outlined'
                                                    : 'contained'
                                            }
                                            size="large"
                                            fullWidth
                                            component="a"
                                            href={config.pixUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => playCue('ui')}
                                        >
                                            {t('support.pix')}
                                        </Button>
                                    )}
                                </Stack>
                            ) : (
                                <Alert severity="info">
                                    {t('support.noLink')}
                                </Alert>
                            )}
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 1 }}
                            >
                                {t('support.noAds')}
                            </Typography>
                        </Box>
                    )}

                    <Typography variant="caption" color="text.secondary">
                        {t('support.noAccount')}{' '}
                        <Link
                            component={RouterLink}
                            to="/about"
                            onClick={onClose}
                        >
                            {t('support.about')}
                        </Link>
                        {' · '}
                        <Link
                            component={RouterLink}
                            to="/privacy-policy"
                            onClick={onClose}
                        >
                            {t('support.privacy')}
                        </Link>
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('support.close')}</Button>
            </DialogActions>
        </Dialog>
    );
};
